import React from "react";
import { MdAdd, MdQuiz, MdUploadFile } from "react-icons/md";
import Modal from "components/ui/Modal";
import FileUploader, { type UploadedFile } from "components/ui/FileUploader";
import { useAppLanguage } from "hooks/useAppLanguage";
import { instructorFolderItemsService } from "services/instructorFolderItemsService";
import { instructorQuizzesService } from "services/instructorQuizzesService";
import type { FolderItemType } from "services/instructorFolderItemsService";
import { toastError, toastSuccess } from "lib/toast";
import { splitFilename } from "lib/filenameParts";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  courseId: number | string;
  folderId: number;
};

function inferType(file: File): FolderItemType {
  const mime = file.type || "";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return "file";
}

export default function NewItemDialog({ open, onClose, onCreated, courseId, folderId }: Props) {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [mode, setMode] = React.useState<"files" | "quiz">("files");
  const [uploaded, setUploaded] = React.useState<UploadedFile[]>([]);
  const [creating, setCreating] = React.useState(false);

  // Quiz form
  const [quizTitle, setQuizTitle] = React.useState("");
  const [passDegree, setPassDegree] = React.useState(0);
  const [maxAttempts, setMaxAttempts] = React.useState(1);

  const resetAll = () => {
    setUploaded([]);
    setQuizTitle("");
    setPassDegree(50);
    setMaxAttempts(1);
  };

  const handleClose = () => {
    if (creating) return;
    resetAll();
    onClose();
  };

  const submitFiles = async () => {
    const done = uploaded.filter((u) => u.status === "done" && u.key);
    if (done.length === 0) {
      toastError(isArabic ? "لم يتم رفع أي ملف بعد" : "No files have finished uploading");
      return;
    }
    setCreating(true);
    let createdCount = 0;
    try {
      for (const u of done) {
        try {
          const type = inferType(u.file);
          // FileUploader keeps the editable display name separately from the
          // physical file name. Use it when creating the item; otherwise the
          // rename field appears to work but the original name is persisted.
          const requestedName = u.displayName?.trim() || u.file.name;
          const title =
            splitFilename(requestedName).base.trim() || splitFilename(u.file.name).base.trim();
          await instructorFolderItemsService.create(courseId, folderId, {
            type,
            title,
            path: u.key ?? null,
            sort_order: 1,
          });
          createdCount++;
        } catch {
          // per-file: surface via interceptor toast
        }
      }
      if (createdCount > 0) {
        toastSuccess(
          isArabic ? `تمت إضافة ${createdCount} عنصر` : `Created ${createdCount} item${createdCount === 1 ? "" : "s"}`
        );
        await onCreated();
        handleClose();
      }
    } finally {
      setCreating(false);
    }
  };

  const submitQuiz = async () => {
    if (!quizTitle.trim()) {
      toastError(isArabic ? "عنوان الاختبار مطلوب" : "Quiz title is required");
      return;
    }
    if (!Number.isFinite(passDegree) || passDegree < 0) {
      toastError(isArabic ? "درجة النجاح غير صحيحة" : "Invalid pass degree");
      return;
    }
    if (!Number.isFinite(maxAttempts) || maxAttempts < 1) {
      toastError(isArabic ? "عدد المحاولات غير صحيح" : "Invalid max attempts");
      return;
    }
    setCreating(true);
    try {
      const quiz = await instructorQuizzesService.create(courseId, folderId, {
        title: quizTitle.trim(),
        pass_degree: Number(passDegree),
        max_attempts: Number(maxAttempts),
      });
      if (quiz?.id) {
        await instructorFolderItemsService.create(courseId, folderId, {
          type: "quiz",
          title: quiz.title,
          quiz_id: quiz.id,
          sort_order: 1,
        });
        await onCreated();
        handleClose();
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isArabic ? "إضافة عناصر" : "Add items"}
      size="lg"
    >
      <div className="flex flex-col gap-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode("files")}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
              mode === "files"
                ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
                : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
            }`}
          >
            <MdUploadFile className="h-5 w-5" />
            {isArabic ? "ملفات متعددة" : "Multi-file"}
          </button>
          <button
            type="button"
            onClick={() => setMode("quiz")}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
              mode === "quiz"
                ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
                : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
            }`}
          >
            <MdQuiz className="h-5 w-5" />
            {isArabic ? "اختبار" : "Quiz"}
          </button>
        </div>

        {mode === "files" ? (
          <>
            <FileUploader
              group="misc"
              multiple
              onChange={setUploaded}
              hint={isArabic ? "ستتحول كل ملف لعنصر في هذا المجلد" : "Each file becomes an item in this folder"}
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={creating}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/10"
              >
                {isArabic ? "إلغاء" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={submitFiles}
                disabled={creating || uploaded.filter((u) => u.status === "done").length === 0}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
              >
                <MdAdd className="h-5 w-5" />
                {isArabic ? "إضافة كعناصر" : "Add as items"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-3">
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 md:col-span-3">
                {isArabic ? "عنوان الاختبار *" : "Quiz title *"}
                <input
                  value={quizTitle}
                  onChange={(e) => setQuizTitle(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </label>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                {isArabic ? "درجة النجاح *" : "Pass score *"}
                <input
                  type="number"
                  min={0}
                  value={passDegree}
                  onChange={(e) => setPassDegree(Number(e.target.value))}
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
                <span className="mt-1 block text-[11px] font-medium text-gray-400">
                  {isArabic
                    ? "بالدرجات (من مجموع درجات الأسئلة)، وليست نسبة مئوية"
                    : "In points (of total question points), not a percentage"}
                </span>
              </label>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                {isArabic ? "أقصى محاولات *" : "Max attempts *"}
                <input
                  type="number"
                  min={1}
                  value={maxAttempts}
                  onChange={(e) => setMaxAttempts(Number(e.target.value))}
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={creating}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/10"
              >
                {isArabic ? "إلغاء" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={submitQuiz}
                disabled={creating}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
              >
                <MdQuiz className="h-5 w-5" />
                {isArabic ? "إنشاء الاختبار" : "Create quiz"}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
